import ReactGA from '@core/utils/reactGA'
import type {PackQueueContext} from '@prisma/client'
import type {ShareMethod, ShareFailReason} from '@core/wall/share'
import type {WallGenerateKind, WallArmedFrom, WallMakeVia, WallSearchFrom, WallWordTappedFrom, WallChipTappedFrom, FailReason, RatioKey} from '@core/wall/contract'

function isTrackingEnabled(): boolean {
    if (typeof window === 'undefined') return false
    try {
        if (window.localStorage?.getItem('gaOptOut') === '1') return false
    } catch {}
    return !!window.ENV?.GA_MEASUREMENT_ID && window.ENV?.ENV === 'production'
}

export const trackPageView = (path: string, title?: string) => {
    if (!isTrackingEnabled()) return
    ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
    })
}

/** Where the terms gate unfolded — `core/consent/useConsentGate.ts`'s only two events name it
 *  (plan 2026_09_01_tos_gate.md §8.8). */
export type ConsentSurface = 'pack_cta' | 'page_verb' | 'column' | 'sheet' | 'after_sign_in'

/** Where the Share door was pressed. */
export type ShareSurface = 'column' | 'sheet'

export type CoreEventMap = {
    // Identity & Session
    // method/trigger: widened for the narcis_funnel event dictionary (spec §2 —
    // `sign_up (method: google|magic_link, trigger: train_gate|tool_gate|direct)`), which fires
    // through THIS shared event (root.tsx is common to every product, so the funnel-specific
    // FunnelEventMap can't own the real emission site). `trigger` is optional so quies/ovid, which
    // never populate the URL param this reads from, stay valid with no plumbing on their side.
    // 'email' is kept alongside 'magic_link' for the same reason — narcis emits 'magic_link'
    // (the dictionary's own vocabulary), other products still emit 'email'.
    sign_up: { method: 'google' | 'facebook' | 'email' | 'magic_link'; trigger?: string }
    // trigger widened for the wall (plan d amendment S5): 'wall' when the sign-in overlay opened
    // from a visitor's first Generate (`GATE_TRIGGER_WALL`, `@core/wall/contract`). Optional so
    // every other sign_in call site (unrelated to the wall) stays valid unchanged.
    sign_in: { method: 'google' | 'facebook' | 'email' | 'magic_link'; trigger?: string }

    // Funnel Gates
    // `trigger` mirrors gate_hit's own field so a row here joins back to the gate_hit that
    // raised it (same trigger + packSlug, same GA4 session). No gate_converted / gate_abandoned
    // pair: "converted" would need a gate flag carried through the OAuth session-stash AND the
    // magic-link session-stash (two new server round-trips beyond the existing `redirectTo`
    // one) just to reattach it at the post-callback sign_in/sign_up — real plumbing, not a
    // client read. "Abandoned" has no honest fire point at all: it is the absence of a signal
    // (tab close, back button), not an event a page can emit. Compute both in GA4 instead, by
    // joining gate_hit -> auth_option_selected -> magic_link_sent -> sign_in/sign_up on
    // trigger/packSlug within a session; a conversion is a session with both ends present, an
    // abandonment is one missing the far end.
    // A gate is counted where a person meets it, not where they press through it, and by the
    // component that draws it: `gate` names which gate, so one event counts every gate on every
    // product. The terms card is the exception that predates the rule: `consent_shown` below.
    gate_hit: { gate: 'signup' | 'paywall' | 'muse' | 'age_terms'; trigger: string }

    // Fires the instant the user commits to an auth method, before completion: the Google
    // <Form reloadDocument> submit in SignInOptions, and the magic-link <Form> submit in
    // MagicLinkForm. Answers "which option did they pick" even when they never come back —
    // gate_hit only tells us a gate was shown. `trigger`/`packSlug` are threaded down from
    // whichever surface rendered the gate (SignupGate's callers, or LoginPage itself), so this
    // joins to the gate_hit that preceded it. Google's is a full-document POST: the `track()`
    // call must be synchronous in onSubmit, with nothing awaited or returned that could cancel
    // the submit — gtag.js queues the hit ahead of the navigation.
    auth_option_selected: { method: 'google' | 'facebook' | 'email'; trigger: string; packSlug?: string }

    // Fires once, client-side, when MagicLinkForm mounts in its "check your inbox" state — i.e.
    // after @core/auth/magic-link.server.ts's action has actually called sendMagicLinkEmail and
    // redirected back with `magic-link-sent=1`. This is the truthful "email sent" signal: a
    // rejected/rate-limited/errored send redirects with `magic-link-error` instead and never
    // reaches this mount. There is no server-side GA4 emitter in this codebase — the client
    // mount is the first point telemetry can honestly run.
    magic_link_sent: { trigger: string; packSlug?: string }

    // Purchase
    checkout_started: { price_id: string; value: number; currency: string }
    purchase_complete: { price_id: string; value: number; currency: string; tokens: number }
    purchase_failed: { price_id: string; error?: string }

    // Agent
    deliver: { product: string }

    // Behavioral
    scroll_depth: { depth: 25 | 50 | 75 | 100; page: string }
    engagement_pulse: { visible_seconds: number; page: string }
    page_exit_intent: { page: string; time_on_page_ms: number }
    // connection_lost/connection_restored removed 2026-08-01 (plan
    // 2026_08_01_ga4_wiring_defect_remediation.md, C1) — see analytics.behavioral.ts's own header
    // doc for why (browser-present, no user action, a collection-policy violation).

    // The wall's search — defined here by wall plan b (`thinking/plans/2026_08_31_wall_plan_b_
    // read_path.md` § GA4) ahead of any caller: `GET /wall` already returns `matched_count` on the
    // seam page (`readWallPage`, `@core/wall/tiles.server.ts`) so plan d's wall UI only has to call
    // this, not compute anything itself. Typed and unused until then.
    //   query_len     — length of the user's `q`, not the string itself (no query text in GA4).
    //   matched_count — the matched segment's total row count, from the same response field.
    //   ghost_shown   — whether the client actually rendered the seam divider for this search
    //                   (it always does today, but the field exists independently of the count so
    //                   a future "hide the ghost when matched_count === 0" UI choice doesn't need
    //                   a new event).
    // The typed query text itself never rides this event (decision 1, `2026_09_21_wall_gaps.md`) —
    // `query_len` is the only trace of it here. The CloudFront access log already carries the raw
    // text for `GET /wall?q=…` (`uri_query`, `growth/cloudfront_log.py`), which is the forensic
    // path for what a visitor actually typed.
    //   from          — how the query began: typed words, a phrase (a suggestion or a tile's chip), a facet value,
    //                   a Recent row or a "with these" chip (`WallSearchFrom`).
    wall_search: {query_len: number; matched_count: number; ghost_shown: boolean; signed_in: boolean; from: WallSearchFrom; profile?: string}
    // A word of the lean removed from the field (it is suppressed until Reset), and the lean reset. `token` is the
    // facet token (`use:wall-art`), a house's own closed list, never typed text.
    wall_lean_dropped: {house: string; token: string; profile?: string}
    wall_lean_reset: {house: string; profile?: string}

    // The wall's write path (review item 5b — owed alongside plan c's server-side implementation,
    // ahead of plan d's client callers, same posture as `wall_search` above). `kind` reuses
    // `contract.ts`'s own `WallGenerateKind` union (S5: "one union, here and in plan d") rather
    // than a second hand-kept literal list; `queue_depth` is the pending-set size the client
    // observed at submit time (before this generate's own row(s) were added).
    // `ratio` is the shape the row will be rendered at — the user's pick when they made one, else
    // the preset nearest what the chain already carries (the ratio is askable at any point of a
    // chain since 2026-08-31, so the shape is no longer implied by `kind`).
    // `tile_id`/`chain` (decision 2, `2026_09_21_wall_gaps.md`): the numeric tile id of the basis,
    // or the chain's own root tile — absent for a from-scratch ghost, which has neither yet
    // (`chains.armedTileId`); `chain` is the chain key `chains.armedChainKey` already computes,
    // always present (`'ghost'` for the ghost itself). Added so a tile can be identified on the
    // event without decoding `basis`'s own debug string.
    wall_generate: {kind: WallGenerateKind; queue_depth: number; ratio: RatioKey; tile_id?: number; chain: string; profile?: string}
    // Fires when the client sees a `pending` row turn `failed` (`Take.fail_reason` set) — the
    // pill's "failed, once" moment (head §11). `reason` is the closed `FailReason` union
    // (`contract.ts`), imported rather than duplicated for the same reason as `WallGenerateKind`.
    wall_generation_failed: {reason: FailReason}
    // The render's own completion (decision 6, `2026_09_21_wall_gaps.md`) — the observation-side
    // mirror of `wall_generation_failed` above: fires once, the instant the client sees a row it
    // still remembers as queued/rendering show up `done` in `/mine` (`Wall.tsx`'s own once-per-row
    // guard, same shape as that event's `failedNotedRef`). `kind` is reconstructed from the DONE
    // take's own persisted shape (`chains.takeGenerateKind`) since the fire-time `kind` never rides
    // the wire to completion — `again` and a same-direction `single` are the one distinction that
    // can't be recovered this way, so both read `single`. `tile_id` is the take's own
    // `root_tile_id`, absent for a from-scratch chain.
    wall_generation_done: {kind: WallGenerateKind; tile_id?: number}

    // The remaining plan d events (§ GA4): `wall_tile_armed` fires wherever the column/sheet opens
    // on a basis — a tile tap, a hover-Generate, a ghost tap, or a page cell landing armed (plan e).
    // `basis` is a compact debug string (`JSON.stringify(Basis)`), not a dimension to segment on.
    // `tile_id`/`chain` — see `wall_generate`'s own doc above; the same pair, on the event that
    // fires when the column/sheet actually opens rather than when it fires.
    // `via` (a `from: 'request'` arm only): the press that asked for it — Enter at request length, ⌘/Ctrl+Enter, or a
    // click on the Make row.
    wall_tile_armed: {house: string; from: WallArmedFrom; basis: string; tile_id?: number; chain: string; via?: WallMakeVia; profile?: string}
    // The ghost is tapped — distinct from wall_tile_armed's own 'ghost' `from` value because this
    // is the from-scratch path's own entry point (BRIEF §4), worth counting on its own before
    // whatever Generate does or doesn't follow it.
    ghost_tapped: Record<string, never>
    // A word door tapped (board `WordsAsDoors`, validated 2026-09-18) — "a distinct event on the
    // tap will show whether they are used." `from` distinguishes the armed caption/sheet cap from
    // an unarmed tile's hover label; `word_len` is the tapped word's own length (the cut direction
    // included), never the word text itself. `tile_id` (decision 2) — the caption's own armed tile,
    // or the tile under the cursor for a hover tap; absent for a from-scratch ghost/chain.
    wall_word_tapped: {house: string; from: WallWordTappedFrom; word_len: number; tile_id?: number}
    // A chip phrase set or lifted, on a tile chip or the pill's × (`WallChipTappedFrom`): `token` is the phrase
    // (taken from a prompt, never typed by the visitor), `active` whether the tap put it in the bar or took it
    // out, `tile_id` the tile whose chip it was (absent for the pill's ×). The touch a viewer profile is built from.
    wall_chip_tapped: {house: string; from: WallChipTappedFrom; token: string; active: boolean; tile_id?: number; profile?: string}
    // The arrival paint (head §11's "unseen done takes appear at the top... then wall_arrival_seen
    // fires once and seenAt is set") — fires once per stamped set, never on a typed query.
    wall_arrival_seen: {n_unseen: number}
    // The pill's own two actions — Continue ↑ (reopens the column on the newest) and × (dismisses
    // the pill for the tab, amendment N6; the chain itself stays in the history).
    pill_continue: Record<string, never>
    pill_dismiss: Record<string, never>
    // The pin door (plan `2026_09_04_wall_pin.md`, BRIEF Iteration II §4) — a stamp on the take,
    // never a render, so no `wall_generate`-style `kind`/`ratio` here. `chain` is the chain key
    // (`chains.chainKeyOf`, e.g. `tile:12`), not the take id — the dimension worth grouping by.
    pin: {house: string; chain: string}
    unpin: {house: string; chain: string}
    // The Share door (plan `2026_10_25_wall_share.md`). share_opened: the door pressed on an own done
    // take — `native` = the system sheet opened at once, `menu` = the menu opened. share_completed: the
    // act landed (a target chosen, the save asked for, the clipboard write resolved). share_failed: it
    // did not; `reason: cancelled` is the reader closing the system sheet. take_page_cta: a recipient
    // on `/take/<uuid>` pressed the door into the house.
    share_opened: {house: string; chain: string; tile_id?: number; surface: ShareSurface; method: 'native' | 'menu'}
    share_completed: {house: string; chain: string; tile_id?: number; surface: ShareSurface; method: ShareMethod}
    share_failed: {house: string; chain: string; surface: ShareSurface; method: ShareMethod; reason: ShareFailReason}
    take_page_cta: {house: string; tile_id?: number}
    // A signed-out visitor's first Generate landed (plan 2026_10_25_toile_first_take_free.md): rows written
    // without an account. `surface`: page_verb = the pack page's nine, wall = one take; `kind` as `wall_generate`.
    free_pick: {house: string; surface: 'wall' | 'page_verb'; kind: WallGenerateKind}
    // The pack page's own verb (plan e § GA4) — "the page asks for nine" (BRIEF §9). Fires once per
    // PRESS, which is the visitor's decision: not on the re-submit that completes it after the
    // sign-in card, which would count one decision twice. An anonymous press therefore reads as
    // `page_verb` followed by `sign_in`/`sign_up` with `trigger: 'page_verb'`, and the batch that
    // lands is the same press.
    //   direction_len — length of the trimmed words, not the words (no direction text in GA4).
    //   chip_used     — whether an authored chip fired it rather than the field; a chip and typed
    //                   words post the identical body, so this is the only place the two differ.
    page_verb: {house: string; pack_slug: string; direction_len: number; chip_used: boolean}
    // The pack page's own render (decision 4, `2026_09_21_wall_gaps.md`) — `page_verb` above is the
    // visitor's decision; this is the write actually landing, fired inside the closure that posts
    // `/continue/pack` (`StageComponent.tsx`'s `submit`), which both the direct press and the
    // post-sign-in return path funnel through — the return path used to call `gate.press` directly
    // and track nothing, leaving every pack-page batch from a signed-out first press untracked.
    page_generate: {pack_slug: string; direction_len: number; chip_used: boolean}
    // The pack stage's look door, "Change this one" (plan 2026_09_23_pack_stage_coda.md § Decided
    // 3) — fired on the door's click, before the client navigation to the wall armed on that look.
    // Read against `page_verb`, it splits the page's two intents: one picture changed vs. the whole
    // set made one's own.
    pack_look_change: {pack_slug: string; look_index: number}

    // The terms gate (plan 2026_09_01_tos_gate.md §8.8) — fired by `useConsentGate.ts` only, never
    // by a surface directly. `consent_shown` fires the first time a generating press lands
    // unaccepted (the idle -> asked transition); `consent_accepted` fires once, on the ticked ->
    // accepted transition (the second press, the localStorage write). A visitor who is already
    // accepted never fires either — the machine short-circuits before either event exists.
    consent_shown: {house: string; surface: ConsentSurface}
    consent_accepted: {house: string; surface: ConsentSurface}

    // Client-side compression found no canvas path (or the canvas path threw); the file went up
    // uncompressed. Fired from `@core/utils/imageCompress`.
    upload_compress_failed: {
        stage: 'offscreen_canvas' | 'canvas_2d' | 'image_bitmap'
        error: string
        has_offscreen_canvas: boolean
        has_create_image_bitmap: boolean
        user_agent: string
    }
} & ToolEventMap

// The free-tools estate (`@core/tools`) fires the same six events on every product that carries it.
// tool_slug is a registry slug, or 'swap' on the one product that has the face-swap tool.
export type ToolEventMap = {
    tool_file_selected: {tool_slug: string; role?: 'input' | 'source' | 'target'; file_size?: number}
    tool_used: {tool_slug: string}
    tool_completed: {tool_slug: string; time_ms?: number}
    tool_failed: {tool_slug: string; error_type?: string; status_code?: number}
    tool_reset: {tool_slug: string}
    tool_download: {tool_slug: string}
}

// The workbench's derived UI stage — single source of truth for the literal union. Exported here so
// both products' `PackHeroStage`/`PackLandingTemplate` type-check against the one definition instead
// of two hand-kept copies drifting independently. Narcis shows the subset in
// `@product/narcis/packs/packStage`.
export type PackVisualStage =
    | 'ad' | 'canvas' | 'previewing' | 'preview' | 'gate' | 'buying'
    | 'no_model' | 'waiting_model' | 'model_ready' | 'done' | 'error'

// The narcis_funnel event dictionary (`thinking/design/handoffs/funnel/docs/2026_07_24_funnel_spec.md`
// §2), verbatim — names, params, contexts. Param keys are snake_case exactly as the spec writes them
// (`pack_slug`, `photo_count`, `resumed_from_pct`, ...), a deliberate departure from this codebase's
// otherwise-universal `packSlug` camelCase convention: the read-first instruction for the step that
// authored this map is explicit that the dictionary is "exact law: event names, params, contexts,
// verbatim." REPLACES `PackModelEventMap` on narcis's funnel surfaces (pack pages, hub, /model, auth,
// My Packs, emails) — see `NarcisEventMap` (products/narcis/utils/analytics.ts). Shared here (not
// product-scoped) so the deferred ovid delta pass intersects the same map instead of hand-copying it.
//
// `sign_up` does NOT flow through `narcisTrack`/`ovidTrack` and is intersected OUT of both
// `NarcisEventMap`/`OvidEventMap`: it fires through THIS file's own `track()`/`CoreEventMap.sign_up`
// (widened above) — the call site is the shared `app/root.tsx`, common to every product, which
// cannot import a product-scoped tracker without breaking the @core/@product boundary. Declared
// here anyway as the dictionary's canonical shape. (`retrain_started`, added 2026-07-25 per spec
// §10, is a DEVIATION from §2's own list — see DEVIATIONS.md.)
//
// `model_ready` and `training_failed` are gone from this dictionary — see `@core/utils/CLAUDE.md`
// for the collection policy that retired them (GA4 collection policy, 2026-07-28): both were
// server-side with no browser present at the moment they described, and both now log structurally
// to CloudWatch from the training poller instead. `model_ready_seen` (below) is stage 4's real
// exit, replacing `model_ready` with the user's own next return.
export type FunnelEventMap = {
    pack_queued: { pack_slug: string; context: PackQueueContext }
    pack_unqueued: { pack_slug: string; context: PackQueueContext }
    sign_up: { method: 'google' | 'facebook' | 'magic_link'; trigger: 'train_gate' | 'tool_gate' | 'direct' }
    // reason is present on a failed verdict when the caller has one to offer — see
    // ModelIntake.tsx's onEvent adapter for why the current wiring leaves it unset (logged in
    // DEVIATIONS.md: the shared `@core/hooks/useModelIntake.ts` deliberately keeps rejection
    // reasons server-only today).
    // n_verified is the running ok-count at the moment this add settled (2026-08-01, plan
    // 2026_08_01_ga4_wiring_defect_remediation.md, Task 2) — forwarded from the same field
    // `useModelIntake.ts` already computes for its own internal events. There is no BigQuery
    // export on these GA4 properties, so per-session event sequencing can't be queried; a count
    // carried on the event itself is what turns the add stream into a readable effort wall
    // (a histogram across n_verified, drop-off measured against the 12-photo minimum).
    intake_photo_added: { verdict: 'ok' | 'failed'; reason?: string; n_verified: number }
    training_started: { photo_count: number }
    // Retrain intake is additive (funnel spec §10, settled 2026-07-25 — DEVIATION, logged in
    // DEVIATIONS.md: this event was defined in §10, not §2's own dictionary list, so §2 needs the
    // row added). Fires INSTEAD of `training_started` for a retrain Start (spec §10's own line;
    // `training_started` stays first-training-only, still "THE conversion point"). kept/added/
    // removed are the diff between the prefill manifest (the currently served set) and the set
    // actually submitted — see `diffRetrainSet` (`@core/hooks/useModelIntake.ts`).
    retrain_started: { photos_kept: number; photos_added: number; photos_removed: number }
    notify_click: { channel: 'email'; target: 'delivery' | 'model' }
    delivery_viewed: { pack_slug: string; via: 'email' | 'account' | 'return' }
    // Invocation-shaped (spec §2): one event for every way a render gets triggered, so composed/
    // freeform styles (a future portrait-composer trigger) need no new event. `trigger` widened
    // 2026-08-01 (plan 2026_08_01_ga4_wiring_defect_remediation.md, §A) so a re-render is no
    // longer indistinguishable from a first tap — see `@core/utils/CLAUDE.md`'s trigger rule for
    // why that distinction matters to the activation bar. `queued` and `composer` stay declared
    // with no live emitter today (queued: no auto-fulfillment engine fires `generate` on a user's
    // behalf; composer: no freeform-composer surface exists yet) — dictionary law per this map's
    // own header comment, not dead code to prune.
    generate: {
        source: 'pack' | 'portrait' | 'composed'
        style_ref: string
        trigger: 'queued' | 'one_click' | 'hub_autostart' | 'regenerate' | 'composer'
    }
    // pack_slug added 2026-08-07 (plan 2026_08_07_pack_stage_instrumentation.md, fix 1): both live
    // call sites are pack-grid download pills that already hold the slug (they build the download
    // filename from it), so the download stage becomes attributable to a pack instead of a single
    // undifferentiated total. Same field name and snake_case shape as `pack_download` below, since
    // both feed the one already-registered `pack_slug` custom dimension. GA4 dimensions collect
    // forward-only: nothing before this deploys carries a slug. The `download`/`pack_download`
    // overlap (DEVIATIONS.md #59) is untouched and still open.
    download: { pack_slug: string; scope: 'single' | 'pack' }
    // price_view removed 2026-08-16: it fired when the pack price row scrolled into view, but
    // Ovid grants every pack on sign-in (`usePackMachine.ts`), so the row it
    // observed shows a struck-through price and a "Free" tag. The event measured "scrolled past a
    // crossed-out number", not willingness to pay, and read in reports as "saw pricing".
    // One advance through the nine looks — the card-as-pager's measurement (designer delta §9).
    // Fires once per advance, from the pager AND from the strip's own thumb clicks, so the four ways
    // of moving through the nine looks are one comparable population.
    //   'tap'   — a click, or Enter/Space, on one of the two card zones
    //   'swipe' — a horizontal touch swipe across the card
    //   'arrow' — the ←/→ keys while a card zone holds focus (the hover discs are feedback drawn
    //             inside the zones, not controls of their own, so clicking one reads as 'tap')
    //   'thumb' — a strip thumb click (the coda's ghost thumb included)
    //   'door'  — the aside's coda door, which pages to the coda (Toile, plan 2026_09_23_pack_stage_coda.md)
    // look_index is the zero-based index landed on. "Looks-seen depth" is derived in analysis from
    // the count of DISTINCT look_index values within a session, deliberately not tracked in client
    // state: a depth counter would have to survive remounts and back-navigation to stay honest,
    // and the distinct count answers the strip-discoverability question directly.
    // visual_stage: which surface was paged. The pager started life on the anonymous entry alone
    // and now runs on the owner's entry and on a delivered run as well, so the entry-depth question
    // delta §9 asks is only answerable if the event says where it fired. Segment on
    // `visual_stage = 'ad'` for that baseline.
    pack_look_advanced: {
        pack_slug: string
        method: 'tap' | 'swipe' | 'arrow' | 'thumb' | 'door'
        look_index: number
        visual_stage: PackVisualStage
    }
    // A tap on a locked or still-rendering strip thumb never reaches `goToLook`: both products'
    // thumbs carry a native `disabled` attribute while `!isDone`, and a disabled button eats the
    // click before any handler sees it (DEVIATIONS.md #105 — the fix that lets this event exist at
    // all is removing that attribute in favor of `aria-disabled`, since a11y state and click
    // eligibility were the same boolean for no real reason). The tap this event measures is not a
    // `pack_look_advanced` variant: nothing moves — `featuredIdx` is untouched, no look is landed
    // on — so it is unlock intent aimed at a specific look, the strip's whole reason to exist, and
    // conflating it with a successful advance would blur two different populations (browsing vs.
    // reaching for something not yet available) into one metric.
    // slot_status: what the tapped index actually held. 'absent' — no slot at that index yet.
    // 'locked' — pending, generation hasn't reached it. 'streaming' — rendering, not landed. 'failed'
    // — the render errored; only ovid's strip paints a distinct visual for this today (narcis's slot
    // machine can reach 'failed' too, its strip just has no separate treatment for it yet), so the
    // value is carried on both products rather than typed narrower.
    pack_look_locked_tapped: {
        pack_slug: string
        look_index: number
        visual_stage: PackVisualStage
        slot_status: 'locked' | 'streaming' | 'failed' | 'absent'
    }
    // Background generation & owner sub-states (funnel spec §9, 2026-07-25 settlement).
    // pack_render_resumed: fires on a page mount that finds a run already partway done — a
    // continuously-open tab never remounts, so this reads as "re-attached after an absence," not
    // "polled again." See PackLandingTemplate.tsx's renderResumedFiredRef for the exact guard.
    pack_render_resumed: { pack_slug: string; slots_done: number }
    // pack_download: the D-del "Download all N" / per-image download surfaces. COLLISION, not
    // resolved (flagged for the designer, DEVIATIONS.md): spec §2 still lists `download`
    // (scope: single|pack), which steps 0-5 shipped and whose call sites are untouched — this is
    // an ADDITIONAL event for the new D-del surfaces only, not a replacement.
    pack_download: { pack_slug: string; scope: 'all' | 'single' }
    // Encounter events (operator decision 2, 2026-07-28) — a deliberate extension beyond spec §2,
    // following the collection policy: a machine-produced outcome is measured by the user's
    // encounter with it, not its completion. Both client-side only, both firing only to a present
    // user — see `@core/utils/CLAUDE.md`.
    //
    // pack_ready_seen: the completed grid renders to a present user. first_view separates the
    // completion moment (this mount watched the run finish) from a later revisit (the page loads
    // directly into the completed state) — derived from whether this mount ever observed the
    // pack rendering, not a persisted flag; see each product's PackLandingTemplate.tsx.
    pack_ready_seen: { pack_slug: string; slot_count: number; first_view: boolean }
    // model_ready_seen: the user's first sight of a finished model — stage 4's real exit,
    // replacing the retired `model_ready` with return rather than GPU throughput. Fires from
    // YourModelBlock's mount guard alongside model_outcome_observed (ovid) / in its place
    // (narcis, which dropped model_outcome_observed at the funnel cutover). first_view (2026-08-01,
    // plan 2026_08_01_ga4_wiring_defect_remediation.md, Task 3) separates the completion moment
    // (this mount watched the model still training before landing on ready) from a later revisit
    // (the page loads straight into ready) — same mechanism as pack_ready_seen's own first_view,
    // a same-mount observation ref, not a persisted flag.
    model_ready_seen: { first_view: boolean }
}

export function track<E extends keyof CoreEventMap>(event: E, params: CoreEventMap[E]): void {
    if (!isTrackingEnabled()) {
        console.debug('[Analytics]', event, params)
        return
    }
    ReactGA.event(event, params)
}

// Stamp every subsequent GA4 hit on this page load with traffic_type='internal',
// the one signal GA4's built-in "Internal Traffic" data filter consumes to drop
// events at ingestion. Called once for allowlisted (founder) sessions — see the
// root loader's isInternalUser gate. A custom user property cannot drive an
// ingestion filter, so traffic_type is the correct vehicle for "strip from GA".
export function markInternalTraffic(): void {
    if (!isTrackingEnabled()) return
    ReactGA.gtag('set', {traffic_type: 'internal'})
}

// GA4 identity, set on every page load and cleared on sign-out.
//
// personId is GA4's user_id slot: a SHA-256 of the normalized account email,
// computed server-side. It is deliberately NOT public.users.id — that table is
// tenant-isolated by @@unique([email, product]), so one human holds a different
// id on narcis than on ovid, and using it here would make the two products
// permanently uncorrelatable. Hashing the email yields the same value on every
// product and device the person signs in on.
//
// dbUserId is the per-product public.users.id, carried as a user property so a
// GA4 row can still be joined back to the product's own tables.
//
// Passing null for either clears it — required on sign-out, otherwise the next
// visitor on a shared device inherits the previous person's identity.
export function setAnalyticsIdentity(personId: string | null, dbUserId: string | null): void {
    if (!isTrackingEnabled()) return
    ReactGA.gtag('set', {user_id: personId})
    ReactGA.gtag('set', 'user_properties', {db_user_id: dbUserId})
}

// GA4 <-> DB session bridge for anonymous visitors.
//
// id is the anonymous public.sessions.id cuid (Prisma `Session` model, webapp/prisma/schema.prisma)
// carried in the dbSessionId cookie. Setting it on every GA4 hit lets analysts and gallery tools join
// GA4 events back to images.generations / images.sentences / images.render_batches, which all share
// sessions.id as a foreign key — the join personId/db_user_id (setAnalyticsIdentity, above) cannot
// provide, since that pair only exists for visitors who actually sign in.
//
// Named "browser_session_id", not "session_id": GA4 reserves `session_id` as an event parameter name
// (see growth/GA4_CONSOLE_SETUP.md), so reusing it here would collide with GA4's own session model
// instead of carrying ours alongside it.
//
// Carried as a GA4 user property — the same vehicle db_user_id uses in setAnalyticsIdentity above,
// and for the same reason: it is the mechanism measured to work on this stack. The earlier form,
// a bare gtag('set', {browser_session_id: id}), asks gtag.js to attach an arbitrary non-reserved key
// to every later event hit as a default parameter. Measured 2026-07-22 across a 7-day window: 0 of
// 8617 narcis events and 0 of 3189 ovid events carried a value, while db_user_id (user property) ran
// 42.6% / 12.2% and quies_session_id (inlined into each event's params) was populated throughout.
// Three mechanisms, three outcomes — only the bare-`set` default-parameter form produced nothing.
//
// Consequence of user scope, accepted deliberately: GA4 reports the LATEST value per user, so events
// collected before a cookie rotation are re-attributed to the newer session id. That makes this a
// join key for "which DB session did this visitor most recently hold", not a per-event stamp. The
// dbSessionId cookie lives 7 days, so rotation is rare; db_user_id carries the identical caveat.
//
// Requires the GA4 custom dimension for browser_session_id to be registered with scope=USER. The
// pre-existing event-scoped registration cannot observe a user property.
export function setBrowserSessionId(id: string | null): void {
    if (!isTrackingEnabled()) return
    ReactGA.gtag('set', 'user_properties', {browser_session_id: id})
}

// visitor_state — the narcis_funnel plan's one funnel axis (Architecture decision 1;
// `thinking/design/handoffs/funnel/docs/2026_07_24_funnel_spec.md` §0: "set on every hit. Every
// report segments by it."). Value comes from the shared root loader's server-side derivation
// (`@core/products/visitorState.server.ts`'s `deriveVisitorState`) — this function only carries it
// onto the GA4 hit, the same vehicle db_user_id/browser_session_id use above. Same product-agnostic
// placement as those two: quies passes 'anon' unconditionally (its root loader default, unchanged),
// which is a correct, harmless value for a product with no training-shift surface.
export function setVisitorState(state: 'anon' | 'signed' | 'training' | 'owner'): void {
    if (!isTrackingEnabled()) return
    ReactGA.gtag('set', 'user_properties', {visitor_state: state})
}
